import React, { useEffect, useState } from 'react'
import { Alert, AlertTitle } from '@material-ui/lab'
import MCETextEditor from './MCETextEditor.jsx'

import { getPageContent, updatePageContent } from '../api.js'

// KPod editor for Market Insights articles.
// All articles are stored in a single `contents` row named "insights"
// (value = JSON array). The whole array is loaded, edited and saved back
// via PATCH; if the row does not exist yet it is created via POST.

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const todayISO = () => new Date().toISOString().slice(0, 10)

const blankArticle = () => ({
  slug: '',
  title: '',
  excerpt: '',
  body: '',
  author: 'Kellands Real Estate',
  date: todayISO(),
  published: false,
})

const Insights = () => {
  const [articles, setArticles] = useState([])
  const [rowId, setRowId] = useState(null)
  const [editing, setEditing] = useState(null) // article being edited
  const [editIdx, setEditIdx] = useState(-1)    // index in articles, -1 = new
  const [slugTouched, setSlugTouched] = useState(false)
  const [error, setError] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  useEffect(() => {
    getPageContent('insights', handleLoad, handleError)
  }, [])

  const handleLoad = (data) => {
    if (data && data.length) {
      setRowId(data[0].id)
      try {
        const parsed = JSON.parse(data[0].value)
        setArticles(Array.isArray(parsed) ? parsed : [])
      } catch (e) {
        setArticles([])
      }
    } else {
      setRowId(null)
      setArticles([])
    }
  }

  const handleError = (err) => setError(err)

  const startNew = () => {
    setEditing(blankArticle())
    setEditIdx(-1)
    setSlugTouched(false)
    setSaveSuccess(false)
    setError(false)
  }

  const startEdit = (article, idx) => {
    setEditing({ ...article })
    setEditIdx(idx)
    setSlugTouched(true) // keep existing slug stable
    setSaveSuccess(false)
    setError(false)
  }

  // Clicking the open article again closes the form
  const toggleEdit = (article, idx) => {
    if (editing && editIdx === idx) {
      setEditing(null)
      setEditIdx(-1)
    } else {
      startEdit(article, idx)
    }
  }

  const update = (field, value) => {
    setEditing((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'title' && !slugTouched) next.slug = slugify(value)
      return next
    })
  }

  const removeArticle = (idx) => {
    const next = articles.filter((_, i) => i !== idx)
    setArticles(next)
    persist(next)
    if (idx === editIdx) { setEditing(null); setEditIdx(-1) }
  }

  const saveArticle = () => {
    setError(false)
    if (!editing.title || !editing.slug) {
      setError({ error_description: 'Title and slug are required.' })
      return
    }
    // slug must be unique (ignoring the article being edited)
    const clash = articles.some((a, i) => a.slug === editing.slug && i !== editIdx)
    if (clash) {
      setError({ error_description: `Slug "${editing.slug}" is already used by another article.` })
      return
    }

    const next = articles.slice()
    if (editIdx === -1) next.push(editing)
    else next[editIdx] = editing

    setArticles(next)
    persist(next, () => {
      // keep editing the saved article (now indexed)
      setEditIdx(next.indexOf(editing))
    })
  }

  // Persist the whole article list to the `insights` contents row.
  // The row is created/seeded server-side by a developer; the editor only
  // ever PATCHes it (a POST creates a row the public API won't read back).
  const persist = (arr, onDone) => {
    if (!rowId) {
      setError({
        error_description:
          'The "insights" content record does not exist yet. Ask a developer to create it before publishing.',
      })
      return
    }
    const payload = {
      name: 'insights',
      type: 'data',
      value: JSON.stringify(arr),
      storageKey: null,
    }
    updatePageContent(
      rowId,
      payload,
      () => { setSaveSuccess(true); onDone && onDone() },
      handleError
    )
  }

  return (
    <div className="kpod-container">
      <h1 className="kpod-main-title">Market Insights</h1>

      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="kpod-infotext" style={{ marginRight: '24px' }}>
          Articles published here appear at <strong>/insights</strong>. Changes save the whole list;
          avoid two people editing at once. Answers/figures are your responsibility before publishing.
        </div>
        <button className="kpod-form-button" onClick={startNew} style={{ whiteSpace: 'nowrap' }}>
          + Add article
        </button>
      </div>

      <div className="column">
        {/* List */}
        {articles.length === 0 ? (
          <div className="kpod-infotext">No articles yet.</div>
        ) : (
          <div className="column kpod-insight-list">
            {articles.map((a, idx) => (
              <div key={a.slug || idx}
                className={`row kpod-insight-item ${editing && idx === editIdx ? 'kpod-insight-item-active' : ''}`}
                onClick={() => toggleEdit(a, idx)}>
                <div className="column" style={{ flex: 1 }}>
                  <div className="kpod-insight-title">{a.title || '(untitled)'}</div>
                  <div className="kpod-insight-meta">
                    {a.date || '—'} &middot; {a.published ? 'Published' : 'Draft'}
                  </div>
                </div>
                <i className="fa fa-times-circle functions-btn"
                  aria-hidden="true"
                  title="Delete article"
                  style={{ color: '#707070', cursor: 'pointer', padding: '4px' }}
                  onClick={(e) => { e.stopPropagation(); removeArticle(idx) }} />
              </div>
            ))}
          </div>
        )}

        {/* Editor */}
        {editing && (
          <div className="column kpod-form-outer" style={{ marginTop: '16px' }}>
            <label className="kpod-form-infotext-header">Title</label>
            <input type="text" value={editing.title}
              onChange={(e) => update('title', e.target.value)} />

            <label className="kpod-form-infotext-header">Slug (URL)</label>
            <input type="text" value={editing.slug}
              onChange={(e) => { setSlugTouched(true); update('slug', slugify(e.target.value)) }} />

            <label className="kpod-form-infotext-header">Date</label>
            <input type="date" value={editing.date}
              onChange={(e) => update('date', e.target.value)} />

            <label className="kpod-form-infotext-header">Author</label>
            <input type="text" value={editing.author}
              onChange={(e) => update('author', e.target.value)} />

            <label className="kpod-form-infotext-header">Excerpt (summary)</label>
            <textarea rows={3} value={editing.excerpt}
              onChange={(e) => update('excerpt', e.target.value)} />

            <label className="kpod-form-infotext-header">Body</label>
            <div className="kpod-form-inner">
              <MCETextEditor
                description={editing.body}
                setDescription={(html) => update('body', html)}
              />
            </div>

            <label className="row" style={{ alignItems: 'center', marginTop: '8px' }}>
              <input type="checkbox" checked={!!editing.published}
                onChange={(e) => update('published', e.target.checked)} />
              <span style={{ marginLeft: '8px' }}>Published (visible on the website)</span>
            </label>

            <div className="row right" style={{ marginTop: '12px' }}>
              {error && error.error_description && (
                <Alert severity="error"><AlertTitle className="alert-title">{error.error_description}</AlertTitle></Alert>
              )}
              {saveSuccess && (
                <Alert severity="success"><AlertTitle className="alert-title">Saved</AlertTitle></Alert>
              )}
              <button className="kpod-form-button" onClick={saveArticle}>Save</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Insights
